import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";

// JCI Platform Logo URL (same as sidebar)
const JMAP_LOGO_URL = "/jci-icon.svg";

const LoginPage = () => {
  const { isAuthenticated, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginMode, setLoginMode] = useState("oauth"); // "oauth" or "password"

  useEffect(() => {
    // Check for error in query params (from Google OAuth)
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const errorEmail = params.get("email");
    
    if (error === "no_access") {
      toast.error(`Access denied for ${errorEmail || 'this account'}. You are not a member of any tenant.`);
    } else if (error) {
      toast.error(`Login failed: ${error}`);
    }

    if (isAuthenticated && !loading) {
      const from = location.state?.from?.pathname || "/app/dashboard";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location]);

  const handleGoogleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use Emergent Auth for Google Sign-In (handles redirect URIs automatically)
    try {
      const redirectUrl = window.location.origin + '/app/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (error) {
      console.error("Google login error:", error);
      toast.error("Failed to connect to Google Sign-In");
    }
  };

  const handleMicrosoftLogin = () => {
    // Microsoft OAuth - redirect to Microsoft login
    const msClientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
    if (!msClientId) {
      alert("Microsoft login is not configured. Please contact your administrator.");
      return;
    }
    // Use the backend URL for the callback since Azure expects /api prefix
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const redirectUri = encodeURIComponent(`${backendUrl}/api/auth/microsoft/callback`);
    const scope = encodeURIComponent("openid profile email User.Read");
    window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${msClientId}&response_type=code&redirect_uri=${redirectUri}&scope=${scope}&response_mode=query`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    
    setIsLoggingIn(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.trim(),
        password: password
      });
      
      if (response.data.session_token) {
        // Store session and user data
        localStorage.setItem("session_token", response.data.session_token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        
        toast.success("Login successful!");
        
        // Use the login function from auth context if available, otherwise navigate
        if (login) {
          login(response.data.session_token, response.data.user);
        }
        
        const from = location.state?.from?.pathname || "/app/dashboard";
        navigate(from, { replace: true });
        window.location.reload(); // Ensure auth state is refreshed
      }
    } catch (error) {
      console.error("Login error:", error);
      const message = error.response?.data?.detail || "Login failed. Please check your credentials.";
      toast.error(message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md shadow-xl border-0" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          {/* Logo */}
          <div className="mx-auto mb-4">
            <img 
              src={JMAP_LOGO_URL} 
              alt="j-MAP" 
              className="w-16 h-16 rounded-xl object-cover"
            />
          </div>
          <CardTitle
            className="text-2xl font-bold text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            JCI Analytics
          </CardTitle>
          <CardDescription className="text-slate-500">
            by JCI Marketing
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6 pt-6">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-slate-800 mb-1">
              Welcome back
            </h2>
            <p className="text-sm text-slate-500">
              Sign in to access your marketing analytics dashboard
            </p>
          </div>

          {/* Login Mode Toggle */}
          <div className="flex rounded-lg bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setLoginMode("oauth")}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                loginMode === "oauth" 
                  ? "bg-white shadow text-slate-900" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Social Login
            </button>
            <button
              type="button"
              onClick={() => setLoginMode("password")}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                loginMode === "password" 
                  ? "bg-white shadow text-slate-900" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Email & Password
            </button>
          </div>

          {loginMode === "oauth" ? (
            <div className="space-y-3">
              {/* Google Login */}
              <Button
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full h-12 text-base font-medium border-2 hover:bg-slate-50 hover:border-[#00A5A8] transition-all"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or</span>
                </div>
              </div>

              {/* Microsoft Login */}
              <Button
                onClick={handleMicrosoftLogin}
                variant="outline"
                className="w-full h-12 text-base font-medium border-2 hover:bg-slate-50 hover:border-[#00A5A8] transition-all"
                data-testid="microsoft-login-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#F25022" d="M1 1h10v10H1z" />
                  <path fill="#00A4EF" d="M1 13h10v10H1z" />
                  <path fill="#7FBA00" d="M13 1h10v10H13z" />
                  <path fill="#FFB900" d="M13 13h10v10H13z" />
                </svg>
                Continue with Microsoft
              </Button>
            </div>
          ) : (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Login Button */}
              <Button
                type="submit"
                disabled={isLoggingIn}
                className="w-full h-12 text-base font-medium bg-[#00A5A8] hover:bg-[#008B8E] transition-all"
                data-testid="password-login-btn"
              >
                {isLoggingIn ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>

              <p className="text-xs text-center text-slate-500">
                Contact your administrator if you need a password set up
              </p>
            </form>
          )}

          <p className="text-xs text-center text-slate-400 pt-4">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </CardContent>
      </Card>

      {/* Background decoration */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-[#00A5A8]/5"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-[#0F172A]/5"></div>
      </div>
    </div>
  );
};

export default LoginPage;
